import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs/index';
import { filter, map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { RazorpayService } from '@services/razorpay-service';
import { AddressFormComponent } from '@shared/address-form/address-form.component';
import { AddressBookI } from '@store/modals';
import { fromActions } from '@store/actions';
import { fromSelectors } from '@store/selectors';
import { DeliveryDateService } from '@services/delivery-date-service/delivery-date.service';
import { AppConfig } from '../app.config';
import { CheckoutParams, CheckoutService } from '@services/checkout-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { Experiment, ReorderKitDetail } from '@cureskin/api-client/src/server-api';
import { ApiClientConstant } from '@cureskin/api-client/src/constant/api-client.constant';
import { RegimenReorderService } from '@services/regimen-reorder-service/regimen-reorder.service';
import { RegimenReorderPriceDetailsData } from '@shared/bottom-sheet-layouts/price-details-bottomsheet/price-details-bottomsheet.component';
import { PricingDetails } from '@shared/to-pay/to-pay.component';
import { PRICE_DETAILS_BOTTOMSHEET_TYPE } from '../app.constants';


@Component({
  selector: 'user-checkout',
  templateUrl: './user-checkout.html',
  standalone: false,
})
export class UserCheckoutComponent implements OnDestroy {
  private readonly regimenReorderService = inject<RegimenReorderService>(RegimenReorderService);

  ui: any;
  user: any;
  regimenId: any;
  typeOfOrder: any;
  products: any;
  messageId: any;
  expectedDeliveryDate: { from?: Date, to?: Date } = {};
  deliveryIntervalInDays: number = 2;
  services: any;
  offerTimer: any = {};
  subscriptions: Subscription[] = [];
  @ViewChild(AddressFormComponent) addressFormComponent: AddressFormComponent;
  defaultAddress$: Observable<AddressBookI> = this.store.select(fromSelectors.selectDefaultAddress);
  formattedAddress$: Observable<string> = this.defaultAddress$.pipe(map((addr) => this.formatAddress(addr)));
  checkoutInProgress$: Observable<boolean> = this.store.select(fromSelectors.selectCheckoutProgress);
  pincode: number;
  orderDeliveryDateToCurrentDateDifference: number;
  newRegimenPricingExperiment: any;
  isEnglishOrHindiUser: boolean = false;
  isCheckoutCtaClicked: boolean = false;
  cart: Table.Cart;
  fromPaymentPage: boolean = false;
  internalUser: boolean = false;
  useCureSkinCash: boolean | undefined;
  experiments: Experiment[] = [];
  acqRebranding2025: boolean = false;
  private checkoutPageOpenLogged: boolean = false;
  private latestDefaultAddress: AddressBookI | null = null;
  readonly ApiClientConstant = ApiClientConstant;
  showRegimenReorderUI: boolean = false;
  regimenClass: string;
  regimenReorderKitDetailsForClass: ReorderKitDetail;
  constructor(public conn: ConnectionService,
    public readonly router: Router,
    private readonly route: ActivatedRoute,
    public readonly appConfig: AppConfig,
    private readonly eventLogger: EventLoggerService,
    private readonly broadcast: BroadcastService,
    private readonly appBridge: AppWebBridgeService,
    public readonly store: Store,
    private readonly razorpayService: RazorpayService,
    public readonly deliveryDateService: DeliveryDateService,
    private readonly experimentService: ExperimentService,
    private readonly location: Location,
    private readonly checkoutService: CheckoutService,
  ) {
    this.razorpayService.initialize();
  }

  async ngOnInit(): Promise<any> {
    this.appBridge.setStatusBarColor('white');
    this.ui = { loading: true };
    const params = this.route.snapshot.queryParams;
    this.validateAndRenderRegimenReorderUI(params);
    await this.conn.updateLocalUser();
    this.user = this.conn.getActingUser();
    this.isEnglishOrHindiUser = [this.appConfig.Shared.Languages.EN, this.appConfig.Shared.Languages.HI].includes(this.user.get('languagePreference'));
    this.internalUser = this.conn.isInternalUser();
    await this.setUserExperiments();
    this.regimenId = params.regimenId;
    this.typeOfOrder = params.type;
    this.products = params.products;
    this.messageId = params.messageId;
    this.services = params.services;
    // Process useCureSkinCash query param
    if (params.useCureSkinCash !== undefined && params.useCureSkinCash !== null) {
      const originalValue = params.useCureSkinCash === 'true' || params.useCureSkinCash === true;
      this.useCureSkinCash = this.processUseCureSkinCashQueryParam(params.useCureSkinCash);
      this.updateUseCureSkinCashQueryParamIfChanged(originalValue);
    }
    // Log checkout pageOpen exactly once:
    // - If default address is fetched, log based on zipCode.
    // - If user is redirected to "new address" (no default address), log defaultAddressExists=false before leaving.
    this.initCheckoutPageOpenTracking();

    await this.processOrder();

    // Keep reacting to any future default-address changes for pincode/delivery date
    this.subscriptions.push(this.defaultAddress$.subscribe(async (address: AddressBookI): Promise<void> => {
      this.latestDefaultAddress = address || null;
      this.pincode = address?.zipCode;
      if (this.pincode) await this.setDeliveryDate();
    }));
    this.ui.loading = false;
    if (params?.from === 'regimenExpiry') {
      this.cart = await this.conn.getCart();
    }
    const fromPaymentFlag = sessionStorage.getItem('fromPaymentPage');
    if (fromPaymentFlag === 'true') {
      this.fromPaymentPage = true;
      sessionStorage.removeItem('fromPaymentPage');
    }
  }

  /**
   * 1. Checks for offer timer experiment & takes the data from variant and pass it to discount-timer component.
   * 2. Consultation type orders doesn't need address so checks out by default.
   * 3. Fetches the default address from address book, if its empty, then redirects to address book creation page in effects.
   */
  async processOrder(): Promise<void> {
    this.sendOrderLogs();
    const experiments = await this.conn.findUserActiveExperiments();
    experiments?.forEach((experiment: any): void => {
      if (experiment.key === 'offer_timer_throughout_checkout') {
        const timeLeft: any = (new Date(Number(experiment.variant.time)).getTime() - new Date().getTime());
        if (timeLeft > 0) {
          this.offerTimer = { endTime: timeLeft,
            title: experiment.variant.title[this.user?.get('languagePreference') || this.appConfig?.Shared?.Languages?.EN] };
        }
      }
    });
    this.store.dispatch(fromActions.AddressBookDefaultFetchBegin({}));
  }

  async setDeliveryDate(): Promise<void> {
    const result = await this.checkoutService.fetchDeliveryDateInfo(
      this.pincode.toString(),
      this.user.get('languagePreference'),
    );
    this.expectedDeliveryDate = result.expectedDeliveryDate;
    this.orderDeliveryDateToCurrentDateDifference = result.dayDifference;
  }

  private sendOrderLogs(): void {
    this.eventLogger.trackEvent('checkout_entry', { username: this.user?.get('username') });
    this.eventLogger.trackInElasticSearch({ username: this.user?.get('username'),
      added: new Date(),
      type: 'webApp',
      message: 'User into checkout page',
      event: 'CHECKOUT_ENTRY' });
  }

  async checkout(replaceUrl?: boolean): Promise<any> {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'use-this-address' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.checkoutService.sendInitialOrderEvent();
    const params = this.getCheckoutParams();
    const payload = await this.checkoutService.buildCheckoutPayloadForOrder(params);
    this.checkoutService.clearShiprocketStateIfNeeded(this.route.snapshot.queryParams);
    this.checkoutService.dispatchCheckoutBegin(payload, replaceUrl);
  
    this.isCheckoutCtaClicked = true;
  }

  /**
   * Generate one line delivery address by appending all fields.
   */
  getDeliveryAddress(defaultAddress: AddressBookI): string {
    const address = [];
    address.push(defaultAddress.buildingDetails);
    address.push(defaultAddress.localityDetails);
    address.push(defaultAddress.city);
    address.push(defaultAddress.state);
    address.push(defaultAddress.zipCode);
    return address.filter((each: string): boolean => !!each).join(', ');
  }

  private formatAddress(address: AddressBookI): string {
    return [
      address?.buildingDetails,
      address?.localityDetails,
      address?.landmark
    ].filter(Boolean).join(', ');
  }

  navigateTo(url: string): void {
    // Preserve core checkout context when navigating away
    const queryParams = Object.fromEntries(
      Object.entries({
        type: this.typeOfOrder,
        regimenId: this.regimenId,
        products: this.products,
        services: this.services,
        messageId: this.messageId,
        useCureSkinCash: this.useCureSkinCash,
      }).filter(([_, value]) => value != null)
    );

    this.router.navigate([url], Object.keys(queryParams).length ? { queryParams } : undefined);
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  /**
   * Process useCureSkinCash query param value based on experiment state and sessionStorage
   * @param queryParamValue - The useCureSkinCash value from query params (string or boolean)
   * @returns Processed boolean value for useCureSkinCash
   */
  private processUseCureSkinCashQueryParam(queryParamValue: string | boolean): boolean {
    const booleanValue = queryParamValue === 'true' || queryParamValue === true;
    if (!this.experimentService.applyCashExperiment) return booleanValue;

    const sessionStorageValue = this.checkForAnySessionStorageUseCureSkinCash();
    return sessionStorageValue !== null ? sessionStorageValue === 'true' : (booleanValue ? false : booleanValue);
  }

  /**
   * Update query params if useCureSkinCash value changed after processing
   */
  private updateUseCureSkinCashQueryParamIfChanged(originalValue: boolean): void {
    if (this.useCureSkinCash !== originalValue) {
      const currentParams = { ...this.route.snapshot.queryParams, useCureSkinCash: this.useCureSkinCash };
      const urlTree = this.router.createUrlTree([], {
        relativeTo: this.route,
        queryParams: currentParams,
        queryParamsHandling: 'merge',
      });
      // Update the browser URL without triggering a new navigation
      this.location.replaceState(urlTree.toString());
    }
  }

  /**
   * Check if there's any sessionStorage value for useCureSkinCash
   * Returns the value if found, null otherwise
   */
  private checkForAnySessionStorageUseCureSkinCash(): string | null {
    // Check sessionStorage for any useCureSkinCash_* key
    // Since we don't have orderId at checkout, we check all keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('useCureSkinCash_')) {
        return sessionStorage.getItem(key);
      }
    }
    return null;
  }

  async ngOnDestroy(): Promise<void> {
    const fromRegimenExpiryBanner = this.route.snapshot.queryParams?.from === 'regimenExpiry';
    const shouldAddProductToCart = !this.isCheckoutCtaClicked && fromRegimenExpiryBanner && !this.fromPaymentPage;
    if (shouldAddProductToCart) {
      const productIds = this.route.snapshot.queryParams?.products?.split(',');

      const productQuantityMap = new Map<string, number>();
      productIds?.forEach((productId: string) => {
        const currentQuantity = productQuantityMap.get(productId) || 0;
        productQuantityMap.set(productId, currentQuantity + 1);
      });

      const cartLineItems = this.cart?.get('lineItems') || [];

      const existingCartMap = new Map<string, number>();
      cartLineItems?.forEach((item: any) => {
        if (item.section === 'regimenExpiry') {
          existingCartMap.set(item.productId, item.quantity);
        }
      });

      for (const [productId, quantity] of productQuantityMap) {
        const existingQuantity = existingCartMap.get(productId) || 0;

        if (existingQuantity !== quantity) {
          await this.conn.addProductToCart({ productId, quantity, section: 'regimenExpiry' });
        }
      }
    }
    this.subscriptions?.forEach((each: Subscription): void => each.unsubscribe());
  }

  async setUserExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments();
    this.experiments?.forEach((experiment: any): void => {
      if (experiment.key === 'add_extra_regimen_products') {
        this.newRegimenPricingExperiment = experiment;
      } else if (experiment.key === "acq_rebranding_2025") {
        this.acqRebranding2025 = true;
      }
    });
  }

  private initCheckoutPageOpenTracking(): void {
    // Case 1: Default address resolved (truthy object in store)
    this.subscriptions.push(
      this.defaultAddress$
        .pipe(
          filter((address: AddressBookI): boolean => address != null),
          take(1),
        )
        .subscribe((address: AddressBookI): void => {
          this.latestDefaultAddress = address || null;
          this.logCheckoutPageOpen(!!address?.zipCode);
        }),
    );

    // Case 2: Redirect to new-address page due to missing default address
    this.subscriptions.push(
      this.router.events
        .pipe(
          filter((event: unknown): event is NavigationStart => event instanceof NavigationStart),
          filter((event: NavigationStart): boolean => (event?.url || '').startsWith('/user/address/new')),
          take(1),
        )
        .subscribe((): void => {
          if (this.checkoutPageOpenLogged) return;
          const defaultAddressExists = !!this.latestDefaultAddress?.zipCode;
          if (!defaultAddressExists) this.logCheckoutPageOpen(false);
        }),
    );
  }

  private logCheckoutPageOpen(defaultAddressExists: boolean): void {
    if (this.checkoutPageOpenLogged) return;
    this.checkoutPageOpenLogged = true;
    const regimenId = this.regimenId ?? this.route?.snapshot?.queryParams?.regimenId;
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({
      regimenId,
      pageName: 'checkout-page',
      defaultAddressExists,
    }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }


  private validateAndRenderRegimenReorderUI(params: any): void {
    this.showRegimenReorderUI = params?.isRegimenReorder === 'true';
    if (this.showRegimenReorderUI) {
      this.regimenClass = params?.regimenClass;
      this.regimenReorderKitDetailsForClass = this.regimenReorderService.getRegimenReorderKitDetailsForClass(this.regimenClass);
    }
  }

  get checkoutKitImage(): string {
    return this.regimenClass === ApiClientConstant.Regimen.Class.HAIR
      ? 'https://cdn.cureskin.com/app/img/dr_holding_purple_kit.png'
      : 'https://cdn.cureskin.com/app/img/dr_holding_orange_kit.png';
  }

  get isHairRegimen(): boolean {
    return this.regimenClass === ApiClientConstant.Regimen.Class.HAIR;
  }

  get regimenReorderPricingDetails(): PricingDetails {
    return {
      totalMrp: this.regimenReorderKitDetailsForClass?.totalMrp || 0,
      totalSP: this.regimenReorderKitDetailsForClass?.totalSP || 0,
      discountInRupees: this.regimenReorderKitDetailsForClass?.discountInRupees || 0,
    };
  }

  get regimenReorderBottomsheetData(): RegimenReorderPriceDetailsData | null {
    if (!this.regimenReorderKitDetailsForClass) return null;
    return {
      type: PRICE_DETAILS_BOTTOMSHEET_TYPE.REGIMEN_REORDER,
      regimenDetails: this.regimenReorderKitDetailsForClass,
      cashUsed: this.regimenReorderService.getCashUsedForClass(this.regimenClass),
      originalDiscount: this.regimenReorderService.getOriginalDiscountForClass(this.regimenClass),
      showDoctorCharges: this.shouldShowDoctorCharges,
    };
  }

  get shouldShowDoctorCharges(): boolean {
    return this.experimentService.regimenReorderExperiment?.variant?.showDoctorCharges ?? false;
  }

  getCheckoutParams(): CheckoutParams {
    return {
      user: this.user,
      typeOfOrder: this.typeOfOrder,
      regimenId: this.regimenId,
      products: this.products,
      messageId: this.messageId,
      services: this.services,
      useCureSkinCash: this.useCureSkinCash,
      showRegimenReorderUI: this.showRegimenReorderUI,
      regimenClass: this.regimenClass,
      newRegimenPricingExperiment: this.newRegimenPricingExperiment,
    }
  }
}
